import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { FlaskConical, History, Play, AlertCircle, Trash2, CheckCircle2, GitCompare, X, Sparkles } from 'lucide-react';
import {
  API_URL, BOT_COLORS, authHeaders, formatUSD, formatPct, formatNum, formatTs,
} from './ops_helpers';

const DEFAULTS_HL = {
  symbols: ['BTC', 'ETH', 'SOL'],
  days: 180,
  sma_fast: 9,
  sma_slow: 21,
  sl_pct: 0.025,
  rr_ratio: 2.5,
  be_at_r: 1.67,
  risk_per_trade_pct: 0.01,
  leverage: 3,
  initial_capital_usd: 10000,
  enable_atr_filter: true,
  enable_htf_filter: true,
  enable_funding_filter: true,
  enable_macro_filter: true,
  enable_regime_filter: false,
  regime_adx_min: 25,
  enable_rsi_filter: false,
  rsi_long_min: 50,
  rsi_long_max: 70,
  rsi_short_min: 30,
  rsi_short_max: 50,
  enable_volume_filter: false,
  volume_min_ratio: 1.0,
  note: '',
};

const PRESET_PROPOSED_BTC = {
  ...DEFAULTS_HL,
  symbols: ['BTC'],
  sl_pct: 0.012,
  rr_ratio: 3.0,
  be_at_r: 2.0,
  risk_per_trade_pct: 0.0072,
  note: 'Config B propuesta - BTC tuning',
};

const tooltipStyle = {
  backgroundColor: '#0a0a0b',
  border: '1px solid #3f3f46',
  borderRadius: '8px',
  fontSize: '12px',
};

const NumberField = ({ label, value, onChange, step, hint }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-zinc-400">{label}</label>
    <Input
      type="number"
      step={step || 'any'}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className="h-8 text-sm bg-zinc-900 border-zinc-700"
      data-testid={`backtest-input-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    />
    {hint && <span className="text-[10px] text-zinc-500">{hint}</span>}
  </div>
);

const Toggle = ({ label, value, onChange }) => (
  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
    <input
      type="checkbox"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      className="accent-cyan-500"
      data-testid={`backtest-toggle-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    />
    {label}
  </label>
);

const SymbolChip = ({ sym, active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`px-3 py-1 rounded-full text-xs font-mono transition-all ${
      active
        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
        : 'bg-zinc-800 text-zinc-500 border border-zinc-700 hover:border-zinc-500'
    }`}
    data-testid={`backtest-sym-${sym}`}
  >
    {sym}
  </button>
);

export default function BacktestLab({ botId = 'hyperliquid' }) {
  const [params, setParams] = useState(DEFAULTS_HL);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);          // ids checked para comparar (max 3)
  const [compareData, setCompareData] = useState(null);       // detalle de los runs en comparativa
  const [showCompare, setShowCompare] = useState(false);

  // --- Alineacion con el bot LIVE (Control HL) ---
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);

  // --- Asesor IA: recomendacion estructurada -> backtest A/B -> aplicar (human-in-the-loop) ---
  const [advLoading, setAdvLoading] = useState(false);
  const [advError, setAdvError] = useState(null);
  const [advData, setAdvData] = useState(null);     // {analysis, recommended_params, current_params}
  const [advBtRunning, setAdvBtRunning] = useState(false);
  const [advBt, setAdvBt] = useState(null);          // {actual, recomendada}

  // --- Optimizador (barrido de parametros) ---
  const [optRunning, setOptRunning] = useState(false);
  const [optResult, setOptResult] = useState(null);
  const [optError, setOptError] = useState(null);
  const [opt, setOpt] = useState({
    sl_min: 0.008, sl_max: 0.03, sl_step: 0.004,
    rr_min: 1.5, rr_max: 4.0, rr_step: 0.5,
    be_values: [1.0, 1.67, 2.0],
    trailing_options: [false, true],
    min_trades: 10,
  });
  const updateOpt = (k, v) => setOpt((o) => ({ ...o, [k]: v }));
  const toggleOptList = (key, val) => setOpt((o) => {
    const has = o[key].includes(val);
    const next = has ? o[key].filter((x) => x !== val) : [...o[key], val];
    return { ...o, [key]: next.length ? next : o[key] };
  });

  const AVAILABLE_SYMBOLS = ['BTC', 'ETH', 'SOL'];

  const fetchHistory = async () => {
    try {
      const r = await fetch(`${API_URL}/api/ops/backtest?bot_id=${botId}&limit=20`, {
        headers: authHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setHistory(data.items || []);
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/ops/backtest?bot_id=${botId}&limit=20`, { headers: authHeaders() });
        if (r.ok && alive) {
          const data = await r.json();
          setHistory(data.items || []);
        }
      } catch (e) {
        // silent
      }
    })();
    return () => { alive = false; };
  }, [botId]);

  const updateParam = (key, val) => setParams((p) => ({ ...p, [key]: val }));

  const toggleSymbol = (sym) => {
    setParams((p) => {
      const has = p.symbols.includes(sym);
      const next = has ? p.symbols.filter((s) => s !== sym) : [...p.symbols, sym];
      return { ...p, symbols: next.length ? next : [sym] };
    });
  };

  const runBacktest = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/api/ops/backtest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ bot_id: botId, params }),
      });
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`HTTP ${r.status}: ${detail.slice(0, 200)}`);
      }
      const data = await r.json();
      setResult(data);
      setSelectedHistoryId(data.run_id);
      fetchHistory();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const optCellCount = useMemo(() => {
    const n = (mn, mx, st) => (st > 0 ? Math.max(0, Math.floor((mx - mn) / st + 1e-9) + 1) : 0);
    return n(opt.sl_min, opt.sl_max, opt.sl_step)
      * n(opt.rr_min, opt.rr_max, opt.rr_step)
      * Math.max(1, opt.be_values.length)
      * Math.max(1, opt.trailing_options.length);
  }, [opt]);

  const runOptimize = async () => {
    setOptRunning(true);
    setOptError(null);
    setOptResult(null);
    try {
      const r = await fetch(`${API_URL}/api/ops/backtest/optimize_hl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ base_params: params, ...opt }),
      });
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`HTTP ${r.status}: ${detail.slice(0, 200)}`);
      }
      setOptResult(await r.json());
    } catch (e) {
      setOptError(e.message);
    } finally {
      setOptRunning(false);
    }
  };

  const applyOptimalToForm = (cell) => {
    if (!cell) return;
    setParams((p) => ({ ...p, sl_pct: cell.sl_pct, rr_ratio: cell.rr_ratio, be_at_r: cell.be_at_r, enable_trailing: cell.trailing }));
  };

  // Carga la config VIVA del bot (Config Remota) en el formulario -> backtest 1:1 con lo que opera.
  const loadLiveConfig = async () => {
    setApplyMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/ops/config/${botId}/admin`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const c = data?.active_config?.params;
      if (!c) {
        setApplyMsg({ type: 'error', text: 'No hay config viva guardada. Guarda una desde la pestaña Control HL.' });
        return;
      }
      setParams((p) => ({
        ...p,
        symbols: c.symbols || p.symbols,
        sma_fast: c.sma_fast ?? p.sma_fast,
        sma_slow: c.sma_slow ?? p.sma_slow,
        sl_pct: c.sl_pct ?? p.sl_pct,
        rr_ratio: c.rr_ratio ?? p.rr_ratio,
        be_at_r: c.trailing_be_at_r ?? p.be_at_r,
        risk_per_trade_pct: c.risk_per_trade_pct ?? p.risk_per_trade_pct,
        leverage: c.leverage ?? p.leverage,
        enable_funding_filter: c.enable_funding_filter ?? p.enable_funding_filter,
        enable_macro_filter: c.enable_macro_blackout ?? p.enable_macro_filter,
        enable_htf_filter: c.enable_htf_bias_filter ?? p.enable_htf_filter,
        enable_regime_filter: c.enable_regime_filter ?? false,
        regime_adx_min: c.regime_adx_min ?? p.regime_adx_min,
        enable_rsi_filter: c.enable_rsi_filter ?? false,
        rsi_long_min: c.rsi_long_min ?? p.rsi_long_min,
        rsi_long_max: c.rsi_long_max ?? p.rsi_long_max,
        rsi_short_min: c.rsi_short_min ?? p.rsi_short_min,
        rsi_short_max: c.rsi_short_max ?? p.rsi_short_max,
        enable_volume_filter: c.enable_volume_filter ?? false,
        volume_min_ratio: c.volume_min_ratio ?? p.volume_min_ratio,
        enable_trailing: c.enable_trailing ?? p.enable_trailing,
        note: `Config viva v${data.active_config.version}`,
      }));
      setApplyMsg({ type: 'ok', text: `Cargada config viva v${data.active_config.version}. Backtest = exactamente lo que opera el bot.` });
    } catch (e) {
      setApplyMsg({ type: 'error', text: `Error: ${e.message}` });
    }
  };

  // Manda los parametros del formulario al bot (Config Remota -> sync lo aplica en ~60s).
  const applyToBot = async () => {
    if (!window.confirm('¿Aplicar estos parámetros al bot Hyperliquid EN VIVO? Se guarda como nueva versión y el bot la aplica en ~60s (reinicio controlado).')) return;
    setApplying(true);
    setApplyMsg(null);
    try {
      const cfg = {
        symbols: params.symbols,
        interval: params.ltf_interval || '1h',
        sma_fast: params.sma_fast, sma_slow: params.sma_slow,
        sl_pct: params.sl_pct, rr_ratio: params.rr_ratio,
        risk_per_trade_pct: params.risk_per_trade_pct, leverage: params.leverage,
        trailing_be_at_r: params.be_at_r,
        enable_funding_filter: params.enable_funding_filter,
        enable_macro_blackout: params.enable_macro_filter,
        enable_htf_bias_filter: params.enable_htf_filter,
        enable_regime_filter: !!params.enable_regime_filter,
        regime_adx_min: params.regime_adx_min,
        enable_rsi_filter: !!params.enable_rsi_filter,
        rsi_long_min: params.rsi_long_min, rsi_long_max: params.rsi_long_max,
        rsi_short_min: params.rsi_short_min, rsi_short_max: params.rsi_short_max,
        enable_volume_filter: !!params.enable_volume_filter,
        volume_min_ratio: params.volume_min_ratio,
        enable_trailing: params.enable_trailing,
      };
      const r = await fetch(`${API_URL}/api/ops/config/${botId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ params: cfg, note: 'Aplicado desde Backtest Lab', activate: true }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.text()).slice(0, 150)}`);
      const data = await r.json();
      setApplyMsg({ type: 'ok', text: `Aplicado al bot (config v${data?.config?.version}). Se refleja en ~60s.` });
    } catch (e) {
      setApplyMsg({ type: 'error', text: `Error al aplicar: ${e.message}` });
    } finally {
      setApplying(false);
    }
  };

  // Pide al Asesor IA una recomendacion ESTRUCTURADA de parametros (lee datos+config reales).
  const askAdvisor = async () => {
    setAdvLoading(true); setAdvError(null); setAdvData(null); setAdvBt(null);
    try {
      const r = await fetch(`${API_URL}/api/ops/advisor/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ bot_id: botId, current_params: params }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.text()).slice(0, 160)}`);
      setAdvData(await r.json());
    } catch (e) {
      setAdvError(e.message);
    } finally {
      setAdvLoading(false);
    }
  };

  // Mezcla la recomendacion sobre los params actuales (lo que se aplicaria).
  const recommendedFullParams = () => ({ ...params, ...(advData?.recommended_params || {}) });

  // Carga la recomendacion en el formulario (el operador revisa y pulsa "Aplicar al bot").
  const loadRecommendationIntoForm = () => {
    if (!advData?.recommended_params) return;
    setParams((p) => ({ ...p, ...advData.recommended_params }));
    setApplyMsg({ type: 'ok', text: 'Recomendación cargada. Revisa los campos y pulsa "Aplicar al bot".' });
  };

  // Backtest A/B: config actual vs recomendada (2 backtests en paralelo).
  const backtestRecommendation = async () => {
    if (!advData) return;
    setAdvBtRunning(true); setAdvBt(null); setAdvError(null);
    try {
      const post = async (pp) => {
        const r = await fetch(`${API_URL}/api/ops/backtest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ bot_id: botId, params: pp }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.text()).slice(0, 120)}`);
        return r.json();
      };
      const [actual, recomendada] = await Promise.all([post(params), post(recommendedFullParams())]);
      setAdvBt({ actual, recomendada });
    } catch (e) {
      setAdvError(e.message);
    } finally {
      setAdvBtRunning(false);
    }
  };

  const loadFromHistory = async (runId) => {
    try {
      const r = await fetch(`${API_URL}/api/ops/backtest/${runId}`, {
        headers: authHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setResult(data);
        setSelectedHistoryId(runId);
      }
    } catch (e) {
      // silent
    }
  };

  const deleteRun = async (runId, e) => {
    e.stopPropagation();
    if (!window.confirm('Eliminar este run del historial?')) return;
    try {
      await fetch(`${API_URL}/api/ops/backtest/${runId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      fetchHistory();
      if (selectedHistoryId === runId) {
        setResult(null);
        setSelectedHistoryId(null);
      }
    } catch (e) { /* silent */ }
  };

  const applyPreset = (preset) => setParams({ ...DEFAULTS_HL, ...preset });

  const toggleCompare = (runId, e) => {
    e.stopPropagation();
    setCompareIds((ids) => {
      if (ids.includes(runId)) return ids.filter((x) => x !== runId);
      if (ids.length >= 3) return ids;  // max 3
      return [...ids, runId];
    });
  };

  const openCompareModal = async () => {
    if (compareIds.length < 2) return;
    try {
      const runs = await Promise.all(
        compareIds.map(async (id) => {
          const r = await fetch(`${API_URL}/api/ops/backtest/${id}`, { headers: authHeaders() });
          return r.ok ? r.json() : null;
        })
      );
      setCompareData(runs.filter(Boolean));
      setShowCompare(true);
    } catch (e) { /* silent */ }
  };

  const closeCompareModal = () => {
    setShowCompare(false);
    setCompareData(null);
  };

  const clearCompare = () => {
    setCompareIds([]);
    closeCompareModal();
  };

  const equityData = useMemo(() => {
    if (!result?.per_symbol) return [];
    // merge daily_equity de todos los symbols (suma)
    const allTs = new Set();
    Object.values(result.per_symbol).forEach((s) => {
      (s.daily_equity || []).forEach((p) => allTs.add(p.ts));
    });
    const sorted = [...allTs].sort();
    return sorted.map((ts) => {
      let total = 0;
      Object.values(result.per_symbol).forEach((s) => {
        const pt = (s.daily_equity || []).find((p) => p.ts === ts);
        if (pt) total += pt.equity;
      });
      return {
        ts,
        label: new Date(ts).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
        equity: parseFloat(total.toFixed(2)),
      };
    });
  }, [result]);

  const initialTotal = (params.initial_capital_usd || 10000) * (result?.params?.symbols?.length || 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* ============== LEFT: FORM ============== */}
      <Card className="lg:col-span-1 bg-zinc-950 border-zinc-800" data-testid="backtest-form-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <FlaskConical className="w-4 h-4 text-cyan-400" />
              Parametros del Backtest
            </CardTitle>
          </div>
          <CardDescription className="text-xs">
            Configura SL/TP/RR/risk y filtros. Datos: Hyperliquid API.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Presets */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs border-zinc-700 hover:bg-zinc-800"
              onClick={() => applyPreset(DEFAULTS_HL)}
              data-testid="preset-actual"
            >
              Config Actual
            </Button>
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs border-cyan-700 hover:bg-cyan-900/40"
              onClick={() => applyPreset(PRESET_PROPOSED_BTC)}
              data-testid="preset-btc-tuning"
            >
              Preset BTC v1.6
            </Button>
          </div>

          {/* Symbols */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Symbols</label>
            <div className="flex gap-2">
              {AVAILABLE_SYMBOLS.map((sym) => (
                <SymbolChip
                  key={sym}
                  sym={sym}
                  active={params.symbols.includes(sym)}
                  onToggle={() => toggleSymbol(sym)}
                />
              ))}
            </div>
          </div>

          {/* Period */}
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-zinc-400">Periodo</label>
              <Select value={String(params.days)} onValueChange={(v) => updateParam('days', parseInt(v))}>
                <SelectTrigger className="h-8 text-sm bg-zinc-900 border-zinc-700" data-testid="backtest-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[30, 60, 90, 180, 365].map((d) => (
                    <SelectItem key={d} value={String(d)}>{d} dias</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <NumberField label="Leverage" value={params.leverage}
              onChange={(v) => updateParam('leverage', parseInt(v) || 1)} step={1} />
          </div>

          {/* SL TP RR */}
          <div className="grid grid-cols-3 gap-2">
            <NumberField label="SL %" value={(params.sl_pct * 100).toFixed(2)}
              onChange={(v) => updateParam('sl_pct', v / 100)} step={0.1}
              hint={`(=${(params.sl_pct * 100).toFixed(2)}%)`} />
            <NumberField label="RR Ratio" value={params.rr_ratio}
              onChange={(v) => updateParam('rr_ratio', v)} step={0.1}
              hint={`TP=${(params.sl_pct * params.rr_ratio * 100).toFixed(2)}%`} />
            <NumberField label="BE@R" value={params.be_at_r}
              onChange={(v) => updateParam('be_at_r', v)} step={0.1}
              hint={`BE@+${(params.sl_pct * params.be_at_r * 100).toFixed(2)}%`} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <NumberField label="Risk per trade %" value={(params.risk_per_trade_pct * 100).toFixed(2)}
              onChange={(v) => updateParam('risk_per_trade_pct', v / 100)} step={0.05}
              hint={`Margin: ${((params.risk_per_trade_pct / params.sl_pct / params.leverage) * 100).toFixed(1)}%`} />
            <NumberField label="Capital inicial USD" value={params.initial_capital_usd}
              onChange={(v) => updateParam('initial_capital_usd', v)} step={1000} />
          </div>

          {/* SMA */}
          <div className="grid grid-cols-2 gap-2">
            <NumberField label="SMA Fast" value={params.sma_fast}
              onChange={(v) => updateParam('sma_fast', parseInt(v) || 1)} step={1} />
            <NumberField label="SMA Slow" value={params.sma_slow}
              onChange={(v) => updateParam('sma_slow', parseInt(v) || 1)} step={1} />
          </div>

          {/* Filtros */}
          <div className="pt-2 border-t border-zinc-800">
            <p className="text-xs text-zinc-400 mb-2">Filtros activos</p>
            <div className="grid grid-cols-2 gap-2">
              <Toggle label="ATR" value={params.enable_atr_filter}
                onChange={(v) => updateParam('enable_atr_filter', v)} />
              <Toggle label="HTF Bias" value={params.enable_htf_filter}
                onChange={(v) => updateParam('enable_htf_filter', v)} />
              <Toggle label="Funding" value={params.enable_funding_filter}
                onChange={(v) => updateParam('enable_funding_filter', v)} />
              <Toggle label="Macro" value={params.enable_macro_filter}
                onChange={(v) => updateParam('enable_macro_filter', v)} />
            </div>
            {/* Filtros alineados con el bot LIVE */}
            <p className="text-xs text-cyan-300/80 mt-3 mb-1">Filtros del bot live (alineados con Control HL)</p>
            <div className="grid grid-cols-3 gap-2">
              <Toggle label="Régimen ADX" value={params.enable_regime_filter}
                onChange={(v) => updateParam('enable_regime_filter', v)} />
              <Toggle label="RSI" value={params.enable_rsi_filter}
                onChange={(v) => updateParam('enable_rsi_filter', v)} />
              <Toggle label="Volumen" value={params.enable_volume_filter}
                onChange={(v) => updateParam('enable_volume_filter', v)} />
            </div>
            {params.enable_regime_filter && (
              <div className="mt-2">
                <NumberField label="ADX mínimo" value={params.regime_adx_min}
                  onChange={(v) => updateParam('regime_adx_min', parseFloat(v) || 0)} step={1} />
              </div>
            )}
            {params.enable_rsi_filter && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                <NumberField label="RSI L-min" value={params.rsi_long_min}
                  onChange={(v) => updateParam('rsi_long_min', parseFloat(v) || 0)} step={1} />
                <NumberField label="RSI L-max" value={params.rsi_long_max}
                  onChange={(v) => updateParam('rsi_long_max', parseFloat(v) || 0)} step={1} />
                <NumberField label="RSI S-min" value={params.rsi_short_min}
                  onChange={(v) => updateParam('rsi_short_min', parseFloat(v) || 0)} step={1} />
                <NumberField label="RSI S-max" value={params.rsi_short_max}
                  onChange={(v) => updateParam('rsi_short_max', parseFloat(v) || 0)} step={1} />
              </div>
            )}
            {params.enable_volume_filter && (
              <div className="mt-2">
                <NumberField label="Vol. ratio mín" value={params.volume_min_ratio}
                  onChange={(v) => updateParam('volume_min_ratio', parseFloat(v) || 0)} step={0.1} />
              </div>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Nota (opcional)</label>
            <Input
              type="text"
              value={params.note}
              onChange={(e) => updateParam('note', e.target.value)}
              placeholder="ej: Test SL tight + RR alto"
              className="h-8 text-sm bg-zinc-900 border-zinc-700"
              data-testid="backtest-note"
            />
          </div>

          {botId === 'hyperliquid' && (
            <div className="space-y-2 pt-2 border-t border-zinc-800" data-testid="advisor-rec-card">
              <p className="text-xs text-violet-300 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Asesor IA → recomendación
              </p>
              <Button size="sm" variant="outline" onClick={askAdvisor} disabled={advLoading}
                className="h-8 text-xs w-full border-violet-700 hover:bg-violet-900/40 text-violet-200"
                data-testid="advisor-ask-btn">
                {advLoading ? 'Analizando datos del bot…' : 'Pedir recomendación al Asesor IA'}
              </Button>
              {advError && (
                <div className="text-xs rounded px-2 py-1 border bg-rose-900/30 border-rose-800 text-rose-300" data-testid="advisor-error">
                  {advError}
                </div>
              )}
              {advData && (
                <div className="space-y-2 rounded-md border border-violet-900/60 bg-violet-950/20 p-2" data-testid="advisor-result">
                  <p className="text-[11px] text-zinc-300 whitespace-pre-wrap leading-relaxed">{advData.analysis}</p>
                  {Object.keys(advData.recommended_params || {}).length > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(advData.recommended_params).map(([k, v]) => {
                          const cur = (advData.current_params && advData.current_params[k] !== undefined) ? advData.current_params[k] : params[k];
                          return (
                            <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 border border-violet-800 text-violet-200" data-testid={`advisor-diff-${k}`}>
                              {k}: {String(cur)} → <b className="text-violet-100">{String(v)}</b>
                            </span>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button size="sm" variant="outline" onClick={backtestRecommendation} disabled={advBtRunning}
                          className="h-8 text-xs border-cyan-700 hover:bg-cyan-900/40 text-cyan-200" data-testid="advisor-backtest-btn">
                          {advBtRunning ? 'Backtesting…' : 'Backtestear actual vs recomendada'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={loadRecommendationIntoForm}
                          className="h-8 text-xs border-violet-700 hover:bg-violet-900/40 text-violet-200" data-testid="advisor-load-btn">
                          Cargar en formulario
                        </Button>
                      </div>
                      {advBt && (
                        <div className="overflow-hidden rounded border border-zinc-800" data-testid="advisor-bt-delta">
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="bg-zinc-900 text-zinc-400">
                                <th className="text-left px-2 py-1">Métrica</th>
                                <th className="text-right px-2 py-1">Actual</th>
                                <th className="text-right px-2 py-1 text-violet-300">Recomendada</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                ['Trades', 'trades', ''],
                                ['Win Rate', 'win_rate_pct', '%'],
                                ['ROI', 'roi_pct', '%'],
                                ['PnL neto', 'net_pnl_usd', '$'],
                              ].map(([label, key, unit]) => {
                                const a = advBt.actual?.aggregate?.[key];
                                const b = advBt.recomendada?.aggregate?.[key];
                                const better = (typeof a === 'number' && typeof b === 'number' && key !== 'trades') ? (b >= a) : null;
                                const fmt = (x) => (x === undefined || x === null) ? '—' : `${unit === '$' ? '$' : ''}${x}${unit === '%' ? '%' : ''}`;
                                return (
                                  <tr key={key} className="border-t border-zinc-800">
                                    <td className="px-2 py-1 text-zinc-300">{label}</td>
                                    <td className="px-2 py-1 text-right font-mono text-zinc-300">{fmt(a)}</td>
                                    <td className={`px-2 py-1 text-right font-mono ${better === true ? 'text-emerald-300' : better === false ? 'text-rose-300' : 'text-violet-200'}`}>{fmt(b)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-[11px] text-zinc-500" data-testid="advisor-no-change">El Asesor no propuso cambios de parámetros.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {botId === 'hyperliquid' && (
            <div className="space-y-2 pt-2 border-t border-zinc-800">
              <p className="text-xs text-zinc-400">Sincronización con el bot live</p>
              <div className="grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" onClick={loadLiveConfig}
                  className="h-8 text-xs border-cyan-700 hover:bg-cyan-900/40" data-testid="bt-load-live-btn">
                  Cargar config viva
                </Button>
                <Button size="sm" variant="outline" onClick={applyToBot} disabled={applying}
                  className="h-8 text-xs border-emerald-700 hover:bg-emerald-900/40 text-emerald-300" data-testid="bt-apply-bot-btn">
                  {applying ? 'Aplicando…' : 'Aplicar al bot'}
                </Button>
              </div>
              {applyMsg && (
                <div data-testid="bt-apply-msg" className={`text-xs rounded px-2 py-1 border ${applyMsg.type === 'ok' ? 'bg-emerald-900/30 border-emerald-800 text-emerald-300' : 'bg-rose-900/30 border-rose-800 text-rose-300'}`}>
                  {applyMsg.text}
                </div>
              )}
            </div>
          )}

          <Button
            onClick={runBacktest}
            disabled={running || params.symbols.length === 0}
            className="w-full bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="run-backtest-btn"
          >
            {running ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin">⏳</span>
                Backtesting... (~{params.days < 60 ? '15s' : params.days < 180 ? '30s' : '60s'})
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Play className="w-4 h-4" /> Run Backtest
              </span>
            )}
          </Button>

          {error && (
            <div className="text-xs text-red-400 bg-red-950/30 border border-red-900 rounded p-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============== CENTER: RESULTS ============== */}
      <Card className="lg:col-span-2 bg-zinc-950 border-zinc-800" data-testid="backtest-results-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              Resultados
            </span>
            {result && (
              <Badge variant="outline" className="font-mono text-xs">
                run_id: {result.run_id}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!result ? (
            <div className="text-center py-16 text-zinc-500 text-sm">
              {running ? (
                <p>Corriendo backtest. Espera unos segundos...</p>
              ) : (
                <p>Configura los parametros a la izquierda y lanza un backtest.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Aggregate KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-zinc-900 rounded p-2">
                  <p className="text-[10px] text-zinc-500 uppercase">Trades</p>
                  <p className="text-xl font-semibold text-zinc-100">{result.aggregate.trades}</p>
                </div>
                <div className="bg-zinc-900 rounded p-2">
                  <p className="text-[10px] text-zinc-500 uppercase">Win Rate</p>
                  <p className="text-xl font-semibold text-zinc-100">{result.aggregate.win_rate_pct.toFixed(1)}%</p>
                </div>
                <div className={`rounded p-2 ${result.aggregate.net_pnl_usd >= 0 ? 'bg-emerald-950/40' : 'bg-red-950/40'}`}>
                  <p className="text-[10px] text-zinc-500 uppercase">Net PnL</p>
                  <p className={`text-xl font-semibold ${result.aggregate.net_pnl_usd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.aggregate.net_pnl_usd >= 0 ? '+' : ''}{formatUSD(result.aggregate.net_pnl_usd)}
                  </p>
                </div>
                <div className={`rounded p-2 ${result.aggregate.roi_pct >= 0 ? 'bg-emerald-950/40' : 'bg-red-950/40'}`}>
                  <p className="text-[10px] text-zinc-500 uppercase">ROI</p>
                  <p className={`text-xl font-semibold ${result.aggregate.roi_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.aggregate.roi_pct >= 0 ? '+' : ''}{result.aggregate.roi_pct.toFixed(2)}%
                  </p>
                </div>
              </div>

              {/* Exits breakdown */}
              <div className="flex gap-2 text-xs flex-wrap">
                <Badge variant="outline" className="border-emerald-700 text-emerald-300">TP: {result.aggregate.tp}</Badge>
                <Badge variant="outline" className="border-red-700 text-red-300">SL: {result.aggregate.sl}</Badge>
                <Badge variant="outline" className="border-amber-700 text-amber-300">BE: {result.aggregate.be}</Badge>
                <Badge variant="outline" className="border-zinc-700 text-zinc-400">Reverse: {result.aggregate.reverse}</Badge>
              </div>

              {/* Per-symbol table */}
              <div>
                <p className="text-xs text-zinc-400 mb-1">Per-symbol</p>
                <Table>
                  <TableHeader>
                    <TableRow className="text-[10px] uppercase">
                      <TableHead>Symbol</TableHead>
                      <TableHead className="text-right">Trades</TableHead>
                      <TableHead className="text-right">WR%</TableHead>
                      <TableHead className="text-right">TP/SL/BE</TableHead>
                      <TableHead className="text-right">PnL</TableHead>
                      <TableHead className="text-right">ROI%</TableHead>
                      <TableHead className="text-right">PF</TableHead>
                      <TableHead className="text-right">MaxDD%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(result.per_symbol).map(([sym, s]) => (
                      <TableRow key={sym}>
                        <TableCell className="font-mono">{sym}</TableCell>
                        <TableCell className="text-right">{s.trades || 0}</TableCell>
                        <TableCell className="text-right">{s.win_rate_pct?.toFixed(1) || '0'}%</TableCell>
                        <TableCell className="text-right text-xs">
                          <span className="text-emerald-400">{s.exits?.tp || 0}</span>/
                          <span className="text-red-400">{s.exits?.sl || 0}</span>/
                          <span className="text-amber-400">{s.exits?.be || 0}</span>
                        </TableCell>
                        <TableCell className={`text-right font-mono ${(s.net_pnl_usd || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatUSD(s.net_pnl_usd || 0)}
                        </TableCell>
                        <TableCell className={`text-right ${(s.roi_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(s.roi_pct || 0).toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right">{s.profit_factor?.toFixed(2) || '—'}</TableCell>
                        <TableCell className="text-right">{s.max_dd_pct?.toFixed(2) || '0'}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Equity curve */}
              {equityData.length > 1 && (
                <div>
                  <p className="text-xs text-zinc-400 mb-1">Equity curve (suma de symbols)</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={equityData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#71717a' }}
                             domain={['auto', 'auto']}
                             tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatUSD(v)} />
                      <ReferenceLine y={initialTotal} stroke="#52525b" strokeDasharray="3 3" label={{ value: 'Inicial', fill: '#71717a', fontSize: 10 }} />
                      <Line type="monotone" dataKey="equity" stroke="#06b6d4" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {result.note && (
                <p className="text-xs italic text-zinc-400">Nota: {result.note}</p>
              )}
              <p className="text-[10px] text-zinc-500">
                Periodo: {new Date(result.period_start).toLocaleDateString('es-ES')} → {new Date(result.period_end).toLocaleDateString('es-ES')} · Duracion del run: {result.duration_sec?.toFixed(1)}s
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============== HISTORY ============== */}
      {/* OPTIMIZADOR (barrido de parametros) */}
      <Card className="lg:col-span-3 bg-zinc-950 border-amber-900/40" data-testid="backtest-optimizer-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-amber-200">
            <FlaskConical className="w-4 h-4" /> Optimizador de parámetros (barrido)
          </CardTitle>
          <CardDescription className="text-xs">
            Prueba muchas combinaciones de <b>Stop Loss</b> × <b>Ratio (TP)</b> × <b>Break-even</b> × <b>Trailing</b>
            sobre los mismos datos y te dice la <b>mejor</b>. Usa la config de la izquierda (símbolos, días, filtros) como base.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <NumberField label="SL min" value={opt.sl_min} step="0.001" onChange={(v) => updateOpt('sl_min', v)} hint="0.008 = 0.8%" />
            <NumberField label="SL max" value={opt.sl_max} step="0.001" onChange={(v) => updateOpt('sl_max', v)} />
            <NumberField label="SL paso" value={opt.sl_step} step="0.001" onChange={(v) => updateOpt('sl_step', v)} />
            <NumberField label="RR min" value={opt.rr_min} step="0.1" onChange={(v) => updateOpt('rr_min', v)} hint="TP = SL × RR" />
            <NumberField label="RR max" value={opt.rr_max} step="0.1" onChange={(v) => updateOpt('rr_max', v)} />
            <NumberField label="RR paso" value={opt.rr_step} step="0.1" onChange={(v) => updateOpt('rr_step', v)} />
            <NumberField label="Mín trades" value={opt.min_trades} step="1" onChange={(v) => updateOpt('min_trades', v)} hint="para ser válida" />
          </div>
          <div className="flex flex-wrap gap-6 items-end">
            <div>
              <div className="text-xs text-zinc-400 mb-1">Break-even (R) a probar</div>
              <div className="flex gap-2">
                {[1.0, 1.5, 1.67, 2.0, 2.5].map((b) => (
                  <button key={b} type="button" onClick={() => toggleOptList('be_values', b)}
                    className={`px-2 py-1 rounded text-xs font-mono ${opt.be_values.includes(b) ? 'bg-amber-500/20 text-amber-200 border border-amber-500/50' : 'bg-zinc-800 text-zinc-500 border border-zinc-700'}`}
                    data-testid={`opt-be-${b}`}>{b}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-1">Trailing</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => toggleOptList('trailing_options', false)}
                  className={`px-2 py-1 rounded text-xs ${opt.trailing_options.includes(false) ? 'bg-amber-500/20 text-amber-200 border border-amber-500/50' : 'bg-zinc-800 text-zinc-500 border border-zinc-700'}`}
                  data-testid="opt-trail-off">OFF</button>
                <button type="button" onClick={() => toggleOptList('trailing_options', true)}
                  className={`px-2 py-1 rounded text-xs ${opt.trailing_options.includes(true) ? 'bg-amber-500/20 text-amber-200 border border-amber-500/50' : 'bg-zinc-800 text-zinc-500 border border-zinc-700'}`}
                  data-testid="opt-trail-on">ON</button>
              </div>
            </div>
            <span className={`text-xs ${optCellCount > 240 ? 'text-red-400' : 'text-zinc-400'}`}>
              {optCellCount} combinaciones {optCellCount > 240 ? '(máx 240 — reduce rangos)' : ''}
            </span>
          </div>
          <Button onClick={runOptimize} disabled={optRunning || optCellCount > 240 || optCellCount === 0}
            className="bg-amber-600 hover:bg-amber-500 text-white gap-2" data-testid="backtest-optimize-btn">
            {optRunning ? 'Buscando mejor combinación…' : <><FlaskConical className="w-4 h-4" /> Buscar mejor combinación</>}
          </Button>
          {optError && (
            <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="optimize-error">
              <AlertCircle className="w-4 h-4" /> <span>{optError}</span>
            </div>
          )}
          {optResult && !optResult.error && (
            <div className="space-y-3" data-testid="optimize-results">
              <div className="grid md:grid-cols-2 gap-3">
                {[['💰 Mejor por PnL', optResult.optimal_by_pnl], ['📈 Mejor por Profit Factor', optResult.optimal_by_profit_factor]].map(([title, c]) => (
                  <div key={title} className="rounded-lg border border-amber-700/40 bg-amber-950/10 p-3">
                    <div className="text-xs text-amber-200 mb-1">{title}</div>
                    {c ? (
                      <>
                        <div className="text-sm font-mono text-zinc-200">
                          SL {(c.sl_pct * 100).toFixed(1)}% · RR {c.rr_ratio} (TP {(c.tp_pct * 100).toFixed(1)}%) · BE {c.be_at_r}R · trail {c.trailing ? 'ON' : 'OFF'}
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                          PnL <span className={c.net_pnl_usd >= 0 ? 'text-emerald-400' : 'text-red-400'}>{formatUSD(c.net_pnl_usd)}</span> · WR {c.win_rate_pct}% · PF {c.profit_factor ?? '—'} · trades {c.trades} · maxDD {c.max_dd_pct}%
                        </div>
                        <Button size="sm" variant="outline" className="mt-2 h-7 text-xs border-amber-700/50 text-amber-200"
                          onClick={() => applyOptimalToForm(c)} data-testid="opt-apply-btn">
                          Aplicar a la config
                        </Button>
                      </>
                    ) : <span className="text-xs text-zinc-500">sin celdas válidas (sube días o baja mín trades)</span>}
                  </div>
                ))}
              </div>
              <div className="text-xs text-zinc-500">
                {optResult.valid_cells}/{optResult.total_cells} válidas · {optResult.duration_sec}s · símbolos: {(optResult.symbols_with_data || []).join(', ')}
              </div>
              <div className="max-h-80 overflow-y-auto border border-zinc-800 rounded">
                <Table>
                  <TableHeader>
                    <TableRow className="text-[10px] uppercase">
                      <TableHead>SL%</TableHead><TableHead>RR</TableHead><TableHead>TP%</TableHead>
                      <TableHead>BE</TableHead><TableHead>Trail</TableHead>
                      <TableHead className="text-right">Trades</TableHead><TableHead className="text-right">WR%</TableHead>
                      <TableHead className="text-right">PnL</TableHead><TableHead className="text-right">PF</TableHead>
                      <TableHead className="text-right">MaxDD%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(optResult.cells || []).slice(0, 60).map((c, i) => (
                      <TableRow key={i} className={i === 0 ? 'bg-amber-950/20' : ''} data-testid={`opt-cell-${i}`}>
                        <TableCell className="font-mono">{(c.sl_pct * 100).toFixed(1)}</TableCell>
                        <TableCell className="font-mono">{c.rr_ratio}</TableCell>
                        <TableCell className="font-mono">{(c.tp_pct * 100).toFixed(1)}</TableCell>
                        <TableCell className="font-mono">{c.be_at_r}</TableCell>
                        <TableCell>{c.trailing ? 'ON' : 'OFF'}</TableCell>
                        <TableCell className="text-right">{c.trades}</TableCell>
                        <TableCell className="text-right">{c.win_rate_pct}</TableCell>
                        <TableCell className={`text-right font-mono ${c.net_pnl_usd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatUSD(c.net_pnl_usd)}</TableCell>
                        <TableCell className="text-right">{c.profit_factor ?? '—'}</TableCell>
                        <TableCell className="text-right">{c.max_dd_pct}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          {optResult && optResult.error && (
            <div className="text-red-400 text-sm">{optResult.error}</div>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-3 bg-zinc-950 border-zinc-800" data-testid="backtest-history-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4 text-zinc-400" />
              Historial de Backtests ({history.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              {compareIds.length > 0 && (
                <Badge variant="outline" className="border-cyan-700 text-cyan-300 text-xs">
                  {compareIds.length}/3 seleccionados
                </Badge>
              )}
              <Button
                size="sm" variant="outline"
                className="h-7 text-xs border-cyan-700 hover:bg-cyan-900/40 disabled:opacity-30"
                disabled={compareIds.length < 2}
                onClick={openCompareModal}
                data-testid="open-compare-btn"
              >
                <GitCompare className="w-3 h-3 mr-1" />
                Comparar ({compareIds.length})
              </Button>
              {compareIds.length > 0 && (
                <Button
                  size="sm" variant="ghost"
                  className="h-7 text-xs text-zinc-400"
                  onClick={clearCompare}
                  data-testid="clear-compare-btn"
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
          <CardDescription className="text-xs">
            Click en checkbox para seleccionar 2-3 runs y compararlos. Click en fila para cargar resultados.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-center text-zinc-500 text-sm py-4">Sin runs previos.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-[10px] uppercase">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Run ID</TableHead>
                  <TableHead>Symbols</TableHead>
                  <TableHead>Dias</TableHead>
                  <TableHead>SL/RR/BE</TableHead>
                  <TableHead className="text-right">Trades</TableHead>
                  <TableHead className="text-right">WR%</TableHead>
                  <TableHead className="text-right">ROI%</TableHead>
                  <TableHead>Nota</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow
                    key={h.run_id}
                    onClick={() => loadFromHistory(h.run_id)}
                    className={`cursor-pointer hover:bg-zinc-900 ${
                      selectedHistoryId === h.run_id ? 'bg-cyan-950/30' : ''
                    }`}
                    data-testid={`history-row-${h.run_id}`}
                  >
                    <TableCell onClick={(e) => toggleCompare(h.run_id, e)}>
                      <input
                        type="checkbox"
                        checked={compareIds.includes(h.run_id)}
                        readOnly
                        className="accent-cyan-500 cursor-pointer"
                        data-testid={`compare-check-${h.run_id}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs">{formatTs(h.finished_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{h.run_id}</TableCell>
                    <TableCell className="text-xs">{(h.params?.symbols || []).join(',')}</TableCell>
                    <TableCell className="text-xs">{h.params?.days}</TableCell>
                    <TableCell className="text-xs font-mono">
                      {((h.params?.sl_pct || 0) * 100).toFixed(2)}%/
                      {h.params?.rr_ratio}/
                      {h.params?.be_at_r}R
                    </TableCell>
                    <TableCell className="text-right text-xs">{h.aggregate?.trades || 0}</TableCell>
                    <TableCell className="text-right text-xs">{h.aggregate?.win_rate_pct?.toFixed(1) || '0'}%</TableCell>
                    <TableCell className={`text-right text-xs font-semibold ${(h.aggregate?.roi_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(h.aggregate?.roi_pct || 0).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-xs text-zinc-400 max-w-[200px] truncate">
                      {h.note || '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost" size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-950/30"
                        onClick={(e) => deleteRun(h.run_id, e)}
                        data-testid={`delete-run-${h.run_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ============== COMPARE MODAL ============== */}
      {showCompare && compareData && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeCompareModal}
          data-testid="compare-modal"
        >
          <Card
            className="bg-zinc-950 border-zinc-700 max-w-7xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GitCompare className="w-5 h-5 text-cyan-400" />
                  Comparativa de Runs ({compareData.length})
                </CardTitle>
                <Button
                  size="sm" variant="ghost"
                  onClick={closeCompareModal}
                  className="h-8 w-8 p-0"
                  data-testid="close-compare-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription className="text-xs">
                Side-by-side. La columna verde es el mejor en cada metrica.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {(() => {
                // Pre-compute best per metric (highest ROI, highest PF, lowest MaxDD, highest WR)
                const rois = compareData.map((c) => c.aggregate?.roi_pct ?? -999);
                const wrs = compareData.map((c) => c.aggregate?.win_rate_pct ?? 0);
                const pfs = compareData.map((c) => {
                  const vals = Object.values(c.per_symbol || {}).map((s) => s.profit_factor || 0);
                  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
                });
                const dds = compareData.map((c) => {
                  const vals = Object.values(c.per_symbol || {}).map((s) => s.max_dd_pct || 0);
                  return vals.length ? Math.max(...vals) : 0;
                });
                const bestROI = rois.indexOf(Math.max(...rois));
                const bestWR = wrs.indexOf(Math.max(...wrs));
                const bestPF = pfs.indexOf(Math.max(...pfs));
                const bestDD = dds.indexOf(Math.min(...dds));

                return (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Metrica</TableHead>
                        {compareData.map((c, i) => (
                          <TableHead key={c.run_id} className="text-xs">
                            <div className="flex flex-col">
                              <span className="font-mono text-cyan-300">{c.run_id}</span>
                              <span className="text-[10px] text-zinc-500">{formatTs(c.finished_at)}</span>
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Symbols</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs font-mono">{(c.params?.symbols || []).join(', ')}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Periodo</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">{c.params?.days}d</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">SL %</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs font-mono">{((c.params?.sl_pct || 0) * 100).toFixed(2)}%</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">TP % (=SL×RR)</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs font-mono">
                            {((c.params?.sl_pct || 0) * (c.params?.rr_ratio || 0) * 100).toFixed(2)}%
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">BE @ R</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">{c.params?.be_at_r}R</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Risk %</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">{((c.params?.risk_per_trade_pct || 0) * 100).toFixed(2)}%</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Filtros</TableCell>
                        {compareData.map((c) => {
                          const f = [
                            c.params?.enable_atr_filter && 'ATR',
                            c.params?.enable_htf_filter && 'HTF',
                            c.params?.enable_funding_filter && 'FUND',
                            c.params?.enable_macro_filter && 'MACRO',
                          ].filter(Boolean).join('+');
                          return <TableCell key={c.run_id} className="text-[10px]">{f || '—'}</TableCell>;
                        })}
                      </TableRow>
                      <TableRow className="border-t-2 border-zinc-700">
                        <TableCell className="text-xs font-semibold">Trades</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">{c.aggregate?.trades || 0}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Win Rate</TableCell>
                        {compareData.map((c, i) => (
                          <TableCell key={c.run_id} className={`text-xs ${i === bestWR ? 'bg-emerald-950/40 text-emerald-300 font-bold' : ''}`}>
                            {c.aggregate?.win_rate_pct?.toFixed(2) || '0'}%
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Net PnL</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className={`text-xs font-mono ${(c.aggregate?.net_pnl_usd || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatUSD(c.aggregate?.net_pnl_usd || 0)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">ROI %</TableCell>
                        {compareData.map((c, i) => (
                          <TableCell key={c.run_id} className={`text-xs font-bold ${i === bestROI ? 'bg-emerald-950/40 text-emerald-300' : (c.aggregate?.roi_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {(c.aggregate?.roi_pct || 0).toFixed(2)}%
                            {i === bestROI && <span className="ml-1 text-[10px]">★</span>}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">PF (avg)</TableCell>
                        {compareData.map((c, i) => (
                          <TableCell key={c.run_id} className={`text-xs ${i === bestPF ? 'bg-emerald-950/40 text-emerald-300 font-bold' : ''}`}>
                            {pfs[i].toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Max DD %</TableCell>
                        {compareData.map((c, i) => (
                          <TableCell key={c.run_id} className={`text-xs ${i === bestDD ? 'bg-emerald-950/40 text-emerald-300 font-bold' : 'text-red-400'}`}>
                            {dds[i].toFixed(2)}%
                            {i === bestDD && <span className="ml-1 text-[10px]">★</span>}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Nota</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs text-zinc-400 italic">{c.note || '—'}</TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
